import { FC } from "react";

import { Claiming } from "@prisma/client";
import { add, isPast } from "date-fns";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

import InfoAfterClaiming from "sections/physicalItemPage/InfoAfterClaiming";
import Owner from "sections/itemsPage/Owner";

import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import InfoBox from "components/InfoBox";
import { INFO_BOX_TYPE } from "components/InfoBox/types";
import CustomLink from "components/CustomLink";
import ItemModel from "components/ItemModel";

import { PAGE_TYPES } from "enums/pageTypes";
import { CHAIN_IDS } from "enums/chainIds";

import { Metadata } from "interfaces/Metadata.interface";

import { isPolygonNetwork } from "utils/blockchain";

import WebExplorerLinksFactory from "services/blockchain/WebExplorerLinksFactory";

interface SectionProductProps {
  metadata: Metadata;
  isOwner?: boolean;
  ownerAddress?: string | null;
  nickname?: string | null;
  isPhysicalClaimed?: boolean;
  physicalClaiming?: Pick<
    Claiming,
    "updatedAt" | "transactionHash" | "chainId" | "nickname"
  > | null;
  itemChainId?: CHAIN_IDS | null;
  mintTransactionHash?: string;
  pageType: PAGE_TYPES;
  children?: JSX.Element | JSX.Element[] | null;
  bgColor?: string | null;
  tokenId?: number;
}

const SectionProduct: FC<SectionProductProps> = ({
  metadata,
  isPhysicalClaimed,
  physicalClaiming,
  isOwner,
  ownerAddress,
  nickname,
  itemChainId,
  mintTransactionHash,
  pageType,
  children,
  bgColor,
  tokenId,
}) => {
  const isDelivering =
    physicalClaiming?.updatedAt &&
    isPast(add(new Date(physicalClaiming.updatedAt), { days: 20 }));

  return (
    <GridContainer spacing={4}>
      <GridItem xs={12} md={6} lg={5}>
        <ItemModel
          image={metadata?.image}
          animationUrl={metadata?.animation_url}
          frontImage={metadata?.data?.front || metadata?.image}
          backImage={metadata?.data?.back}
          modelHash={metadata?.modelHash}
          bgColor={bgColor}
        />
      </GridItem>
      <GridItem xs={12} md={6} lg={7}>
        {(isPhysicalClaimed || !!physicalClaiming) && <InfoAfterClaiming />}

        <Typography
          component="h2"
          variant="h2"
          fontWeight={700}
          gutterBottom
          sx={{ textAlign: { xs: "left" } }}
        >
          {metadata?.name.includes("#")
            ? metadata.name
            : `${metadata?.name} #${tokenId}`}
        </Typography>

        <Typography gutterBottom mb={3} sx={{ whiteSpace: "pre-line" }}>
          {metadata.description}
        </Typography>

        {pageType === PAGE_TYPES.VOUCHER &&
          (isPhysicalClaimed || !!physicalClaiming) && (
            <Box mb={3}>
              <InfoBox type={INFO_BOX_TYPE.INFO}>
                {isOwner ? "Congratulations, your " : "This "}
                item has been redeemed correctly!
                {isDelivering && isOwner
                  ? " Now, it is time to wait for the delivery! "
                  : " "}
                {physicalClaiming?.transactionHash ? (
                  <Box component="span" mt={1}>
                    Check the redeem transaction on{" "}
                    <CustomLink
                      href={WebExplorerLinksFactory.tx(
                        physicalClaiming?.chainId as CHAIN_IDS,
                        physicalClaiming?.transactionHash,
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {isPolygonNetwork(physicalClaiming?.chainId as CHAIN_IDS)
                        ? "Polygonscan"
                        : "Etherscan"}
                    </CustomLink>
                    !
                  </Box>
                ) : null}
              </InfoBox>
            </Box>
          )}

        {ownerAddress && (
          <Owner
            data={{
              metamaskAddress: ownerAddress,
              nickname: physicalClaiming?.nickname || nickname || "",
            }}
            itemChainId={itemChainId ?? null}
            tokenOwner={ownerAddress}
            mintTransactionHash={mintTransactionHash}
          />
        )}
        {children}
      </GridItem>
    </GridContainer>
  );
};

export default SectionProduct;
